import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Folder, MoreVertical, Pencil, Trash2, Users } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export interface DocumentFolderTile {
  id: number;
  name: string;
  description?: string | null;
  documentCount?: number;
  visibleToEmployees?: boolean;
}

interface FolderTilesProps {
  folders: DocumentFolderTile[];
  onOpenFolder: (folder: DocumentFolderTile) => void;
  onRenameFolder?: (folderId: number, newName: string) => Promise<void> | void;
  onDeleteFolder?: (folderId: number) => Promise<void> | void;
  onToggleEmployeeVisibility?: (
    folderId: number,
    visible: boolean
  ) => Promise<void> | void;
}

export function DocumentFolderTiles({ folders, onOpenFolder, onRenameFolder, onDeleteFolder, onToggleEmployeeVisibility }: FolderTilesProps) {
  const [renaming, setRenaming] = useState<null | { id: number; current: string }>(null);
  const [newName, setNewName] = useState("");
  const [deleting, setDeleting] = useState<null | { id: number; name: string }>(null);

  const startRename = (folder: DocumentFolderTile) => {
    setRenaming({ id: folder.id, current: folder.name });
    setNewName(folder.name);
  };

  const confirmRename = async () => {
    if (!renaming) return;
    const value = newName.trim();
    if (!value || value === renaming.current) {
      setRenaming(null);
      return;
    }
    try { await onRenameFolder?.(renaming.id, value); } finally { setRenaming(null); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try { await onDeleteFolder?.(deleting.id); } finally { setDeleting(null); }
  };

  if (!folders || folders.length === 0) return null;

  return (
    <>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {folders.map((f) => (
        <Card
          key={f.id}
          className="relative hover:bg-muted/40 transition-colors"
        >
          <div
            role="button"
            tabIndex={0}
            onClick={(e) => {
              // Avoid opening folder when clicking menu
              if ((e.target as HTMLElement).closest('[data-folder-menu]')) return;
              onOpenFolder(f);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onOpenFolder(f);
            }}
            className="flex flex-col h-full"
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium truncate flex items-center gap-2">
                <Folder className="h-4 w-4 text-amber-500" />
                <span className="truncate" title={f.name}>{f.name}</span>
                {f.visibleToEmployees && (
                  <Users
                    className="h-3.5 w-3.5 shrink-0 text-blue-500"
                    aria-label="Visible to employees"
                  />
                )}
              </CardTitle>
              {(onRenameFolder || onDeleteFolder || onToggleEmployeeVisibility) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild data-folder-menu>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={(e) => { e.stopPropagation(); }}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-40"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {onRenameFolder && (
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); startRename(f); }}
                      >
                        <Pencil className="mr-2 h-4 w-4" /> Rename
                      </DropdownMenuItem>
                    )}
                    {onToggleEmployeeVisibility && (
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleEmployeeVisibility(f.id, !f.visibleToEmployees);
                        }}
                      >
                        <Users className="mr-2 h-4 w-4" />
                        {f.visibleToEmployees
                          ? "Hide from employees"
                          : "Show to employees"}
                      </DropdownMenuItem>
                    )}
                    {onRenameFolder && onDeleteFolder && <DropdownMenuSeparator />}
                    {onDeleteFolder && (
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={(e) => { e.stopPropagation(); setDeleting({ id: f.id, name: f.name }); }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </CardHeader>
            <CardContent className="mt-auto">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {f.description || 'Folder'}
                </p>
                {f.documentCount !== undefined && (
                  <span className="text-xs font-medium text-muted-foreground ml-2">
                    {f.documentCount} {f.documentCount === 1 ? 'document' : 'documents'}
                  </span>
                )}
              </div>
            </CardContent>
          </div>
        </Card>
      ))}
  </div>
    {/* Rename Dialog */}
    <Dialog open={!!renaming} onOpenChange={(open) => { if (!open) setRenaming(null); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename Folder</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="folder-new-name">New name</Label>
            <Input id="folder-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') confirmRename(); }} autoFocus />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setRenaming(null)}>Cancel</Button>
          <Button onClick={confirmRename} disabled={!newName.trim() || newName.trim() === renaming?.current}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  {/* Delete Confirm Dialog */}
    <Dialog open={!!deleting} onOpenChange={(open) => { if (!open) setDeleting(null); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Folder</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2 text-sm">
          <p>Are you sure you want to delete the folder <span className="font-medium">{deleting?.name}</span>? This will recursively delete all subfolders and documents inside. This action cannot be undone.</p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
          <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}


